<script setup lang="ts">
import type { NavigationItem } from '~/types/navigation'
import type { Profile } from '~/types/profile'
import { reloadNuxtApp } from "nuxt/app";

const router = useRouter()

defineProps({
	navigation: {
		type: Array<NavigationItem>,
		required: true,
	},
	desktop_navigation: {
		type: Array<NavigationItem>,
		required: true,
	},
	isAuthed: {
		type: Boolean,
		required: true,
	},
	profile: {
		type: Object as () => Profile | undefined,
		required: false,
	},
	submenu_logged_in: {
		type: Array<NavigationItem>,
		required: false,
		default: () => [],
	},
	submenu_logged_out: {
		type: Array<NavigationItem>,
		required: false,
		default: () => [],
	},
})

const emits = defineEmits(['toggleSearch'])

// Refresh the page
const handleRefresh = () => {
	reloadNuxtApp({
		path: router.currentRoute.value.path,
		ttl: 0,
	})
};

</script>

<template>
	<ClientOnly>
		<ion-refresher slot="fixed" @ionRefresh="handleRefresh()">
			<ion-refresher-content></ion-refresher-content>
		</ion-refresher>
		<ion-menu menu-id="main-menu" content-id="main-content" side="start" type="overlay">
			<ion-content :key="router.currentRoute.value.path" color="white" :scroll-y="false">
				<div class="space-y-1 pb-3 pt-2">
					<ion-menu-toggle v-for="item in navigation">
						<ion-item
							v-if="item.comingSoon"
							:key="item.name"
							lines="none"
							class="menu-item text-gray-400"
							color="transparent">
							<Icon slot="start" :name="item.icon || ''" size="24px" class="mr-2" />
							{{ item.name }}
						</ion-item>

						<ion-item
							v-else
							lines="none"
							class="menu-item"
							color="transparent"
							:class="[
								item.current
									? 'border-green-500 bg-green-50 text-green-700'
									: 'border-transparent text-gray-600 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-800',
								'block border-l-4 py-2 text-base font-medium cursor-pointer',
							]"
							:aria-current="item.current ? 'page' : undefined"
							@click="$navigate(item.href)">
							<Icon slot="start" :name="item.icon || ''" size="24px" class="mr-2" />
							{{ item.name }}
						</ion-item>
					</ion-menu-toggle>
				</div>
				<div class="border-t border-gray-200 pb-3 pt-4">
					<div v-if="isAuthed && profile?.id">
						<ion-menu-toggle v-for="item in submenu_logged_in">
							<ion-item
								:key="item.name"
								lines="none"
								color="transparent"
								:class="[
									item.current
										? 'border-green-500 bg-green-50 text-green-700'
										: 'border-transparent text-gray-600 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-800',
									'block border-l-4 py-2 text-base font-medium cursor-pointer',
								]"
								:aria-current="item.current ? 'page' : undefined"
								@click="$navigate(item.href)">
								<Icon slot="start" :name="item.icon as string" size="24px" class="mr-2" />
								{{ item.name }}
							</ion-item>
						</ion-menu-toggle>
					</div>
					<div v-else>
						<ion-menu-toggle v-for="item in submenu_logged_out">
							<ion-item
								:key="item.name"
								lines="none"
								color="transparent"
								:class="[
									item.current
										? 'border-green-500 bg-green-50 text-green-700'
										: 'border-transparent text-gray-600 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-800',
									'block border-l-4 py-2 text-base font-medium',
								]"
								:aria-current="item.current ? 'page' : undefined"
								@click="$navigate(item.href)">
								<Icon slot="start" :name="item.icon as string" size="24px" class="mr-2" />
								{{ item.name }}
							</ion-item>
						</ion-menu-toggle>
					</div>
				</div>
			</ion-content>
		</ion-menu>

		<ion-page id="main-content">
			<ion-header class="ion-no-border max-w-7xl mx-auto sm:px-4 lg:px-4 block lg:hidden">
				<ion-toolbar>
					<ion-buttons slot="start">
						<ion-menu-button />
					</ion-buttons>

					<div class="flex items-center">
						<ion-menu-toggle>
							<img src="~/public/logo-long.png" class="desktop-logo cursor-pointer ml-4" />
						</ion-menu-toggle>
					</div>

					<ion-buttons slot="end">
						<Icon name="ph:magnifying-glass"
						class="size-6 mr-2 cursor-pointer"
						@click="emits('toggleSearch')"
						/>
						<Icon
							v-if="profile"
							name="ph:bell-ringing-duotone"
							class="size-6 shake bell mr-2"
							aria-hidden="true" />
						<Avatar
							v-if="profile?.id"
							hide-popover
							:profile="profile"
							:self="profile"
							:is-authed="isAuthed"
							:size="8"
							type="rounded"
							to="/profile"
							class="mr-2" />
					</ion-buttons>
				</ion-toolbar>
			</ion-header>

			<ion-header class="ion-no-border max-w-7xl mx-auto px-4 py-1 hidden lg:block">
				<ion-toolbar>
					<div class="flex flex-row items-center">
						<ion-menu-toggle>
							<img src="~/public/logo-short.png" class="desktop-logo cursor-pointer" />
						</ion-menu-toggle>

						<div id="desktop-menu" :key="router.currentRoute.value.path" class="main-nav ml-12 gap-8">
							<div
								v-for="item in desktop_navigation"
								:key="item.name"
								class="main-nav-item flex flex-col">
								<span v-if="item.comingSoon" class="text-gray-400">{{ item.name }}</span>

								<router-link
									v-else
									:active="item.current"
									:to="item.href"
									:class="[item.current ? 'router-link-active' : '']"
									>{{ item.name }}</router-link
								>
							</div>

							<ion-menu-toggle>
								<div class="main-nav-item cursor-pointer hover:text-green-400">More...</div>
							</ion-menu-toggle>
						</div>
					</div>

					<ion-buttons slot="end">
						<Icon
								name="ph:magnifying-glass"
								class="size-6 mr-2 cursor-pointer"
								@click="emits('toggleSearch')"
							/>
						<div v-if="isAuthed && profile?.id" class="flex items-center">
							<Icon
								v-if="profile"
								name="ph:bell-ringing-duotone"
								class="size-6 shake bell m2-4"
								aria-hidden="true" />
							<Avatar
								v-if="profile?.id"
								hide-popover
								:profile="profile"
								:self="profile"
								:is-authed="isAuthed"
								:size="8"
								type="rounded"
								to="/profile"
								class="mr-2" />
						</div>
						<div v-else>
							<RouterLink to="/login" class="btn-primary mr-2">Login</RouterLink>
						</div>
					</ion-buttons>
				</ion-toolbar>
			</ion-header>

			<slot />
		</ion-page>
	</ClientOnly>
</template>

<style scoped>
.bell:hover {
	animation: shake 1s infinite;
}

.bell:focus {
	animation: shake 1s infinite;
}

ion-item {
	--background: transparent;
}

ion-content {
	--background: white;
}
</style>
